import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';
import localConfig from '../config';
import { getApiErrorMessage } from './getApiErrorMessage';

const baseAPIURL = window.config.apiUrl;
const apiKey = localConfig.apiKey;

function normalizeBaseUrl(url: string): string {
  return url.endsWith('/') ? url : `${url}/`;
}

function normalizeRequestArgs(args: string | FetchArgs): string | FetchArgs {
  if (typeof args === 'string') {
    return args.replace(/^\/+/, '');
  }

  return {
    ...args,
    url: args.url.replace(/^\/+/, ''),
  };
}

function requestUrlFromArgs(args: unknown): string {
  if (typeof args === 'object' && args !== null && 'url' in args) {
    return String((args as { url: string }).url);
  }
  return '';
}

const customFetchBaseQuery: BaseQueryFn<string | FetchArgs> = async (args, api, extraOptions) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: normalizeBaseUrl(baseAPIURL),
    prepareHeaders: (headers) => {
      if (apiKey) headers.set('x-api-key', apiKey);
      return headers;
    },
  });
  const normalizedArgs = normalizeRequestArgs(args);
  const normalizedRequestUrl = requestUrlFromArgs(normalizedArgs);
  const resolvedRequestUrl = normalizedRequestUrl.startsWith('http')
    ? normalizedRequestUrl
    : `${normalizeBaseUrl(baseAPIURL)}${normalizedRequestUrl.replace(/^\/+/, '')}`;

  if (/move-in-cost/.test(normalizedRequestUrl)) {
    console.debug('[RTK fetchBaseQuery] move-in-cost request', {
      normalizedRequestUrl,
      resolvedRequestUrl,
      appOrigin: window.location.origin,
      apiBaseUrl: normalizeBaseUrl(baseAPIURL),
    });
  }

  const result = await baseQuery(normalizedArgs, api, extraOptions);

  if (result.error) {
    const { status } = result.error;
    const errorMessage = getApiErrorMessage(result.error);
    const url = requestUrlFromArgs(args);
    // RTK uses relative paths (e.g. `facilities/123/move-in-cost`), not `/facilities/...`
    const isMoveInSubmit = /facilities\/[^/]+\/move-in$/.test(url);
    const isMoveInCostRequest = /facilities\/[^/]+\/move-in-cost$/.test(url);

    if (errorMessage) {
      const unitNoLongerAvailableMsg = 'This unit is no longer available. Please go back and select a different unit';
      const skipToast =
        errorMessage === unitNoLongerAvailableMsg ||
        isMoveInSubmit ||
        isMoveInCostRequest;
      if (!skipToast) {
        toast.error(errorMessage);
      }
    }
    // Handle specific status codes
    switch (status) {
      case 500:
        console.error(`Internal Server Error: ${errorMessage || 'Please try again later.'}`);
        break;
      case 401:
        sessionStorage.removeItem("bearerToken");
        sessionStorage.removeItem("refreshToken");
        window.location.assign((window as any).config?.identityFrontend || '/login');
        break;
      case 403:
        console.error('Access forbidden: You do not have permission to access this resource.');
        break;
      case 404:
        console.error('Resource not found for endpoint');
        break;
      default:
        break;
    }
  }

  return result;
};

export const baseApi = createApi({
  baseQuery: customFetchBaseQuery,
  tagTypes: ['Deals'],
  endpoints: () => ({}), // Define base endpoints here if necessary
});
